import type { Fall, Faelle, Verteilstand, Verteilzeile, Zuteilung } from "../faelle";
import { arbeitstagVorher, werktagAb } from "../verteilen";
import * as A from "./daten";

/* ══════════════════════════════════════════════════════════════════
   Beispielfälle für den Attrappen-Betrieb (Prototyp, Durchklicken).

   Nachgebaut aus dem Testbestand der Cloud vom 24.09.2026 — dieselben
   Arten und Stufen, relativ zum heutigen Tag, damit die drei Spalten der
   Startseite und das Personal-Band im Prototyp nicht leer stehen.
   ══════════════════════════════════════════════════════════════════ */

const tag = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const zeit = (n: number, uhr = "07:00:00") => `${tag(n)}T${uhr}.000Z`;

const ICH = "attrappe-ich";
const KRAN = { id: "attrappe-kran", name: "Machine Kranfahrer" };

const f = (teil: Partial<Fall> & Pick<Fall, "schluessel" | "art" | "form" | "stufe" | "gruppe">): Fall => ({
  eskalation: null, besitzer: null, vorgang: null, kunde: null, kennung: null, belegung_id: null,
  faellig: null, seit: null, daten: {}, weg: null, ...teil,
});

export const FAELLE: Faelle = {
  ich: ICH,
  gruppen: ["disposition", "vertrieb", "buchhaltung", "verwaltung"],
  faelle: [
    f({ schluessel: "defekt_draussen:1", art: "defekt_draussen", form: "aufgabe", stufe: "blockiert", gruppe: "disposition",
      kennung: "RP-02", kunde: "Musterfirma GmbH", vorgang: "V-2026-0016", seit: tag(-6), weg: "/kalender" }),
    f({ schluessel: "qualifikation:1", art: "qualifikation", form: "aufgabe", stufe: "ueberfaellig", gruppe: "verwaltung",
      kennung: "Sebastian Ott", faellig: tag(-2), daten: { qualifikation: "KRANSCHEIN" }, weg: "/ressourcen" }),
    f({ schluessel: "nachfassen:1", art: "nachfassen", form: "aufgabe", stufe: "heute", gruppe: "vertrieb",
      kennung: "V-2026-0023", kunde: "Beispiel GmbH", vorgang: "V-2026-0023", besitzer: { id: ICH, name: "Ich" },
      faellig: tag(0), weg: "/angebote/V-2026-0023" }),
    f({ schluessel: "nachfassen:2", art: "nachfassen", form: "aufgabe", stufe: "demnaechst", gruppe: "vertrieb",
      kennung: "V-2026-0022", kunde: "Musterfirma GmbH", vorgang: "V-2026-0022", besitzer: { id: ICH, name: "Ich" },
      faellig: tag(13), weg: "/angebote/V-2026-0022" }),
    f({ schluessel: "werkstatt_vorbei:1", art: "werkstatt_vorbei", form: "aufgabe", stufe: "ueberfaellig", gruppe: "technik",
      eskalation: "disposition", kennung: "RL-09", seit: tag(-2), weg: "/ressourcen" }),
    f({ schluessel: "werkstatt_vorbei:2", art: "werkstatt_vorbei", form: "aufgabe", stufe: "ueberfaellig", gruppe: "technik",
      eskalation: "disposition", kennung: "RL-11", seit: tag(-2), weg: "/ressourcen" }),
    /* **Der Fall von V-2026-0015** (Luca, 25.09.2026), nachgestellt an einem
       Auftrag, den es im Prototyp gibt: Die Vorbereitung hat der Kranfahrer
       genommen und nicht abgeschlossen, die Übergabe von gestern hat kein
       Protokoll, und die AB drüben weicht ab. Dazu eine zweite Übergabe morgen,
       an einem anderen Gerät, mit Fahrer — genau die, auf die Luca „überfällig"
       bezogen hatte. Zu sehen auf der Startseite und im Band „Was hier
       hakt" am Auftrag. */
    f({ schluessel: "vorbereitung:1", art: "vorbereitung", form: "aufgabe", stufe: "blockiert", gruppe: "technik",
      eskalation: "disposition", kennung: "KR-01", kunde: "Solar Nord GmbH", vorgang: "V-2026-0151",
      besitzer: KRAN, belegung_id: "b6", faellig: zeit(-1),
      daten: { besitz: "genommen", genommen_am: zeit(-2), vorbereiten_ab: zeit(-2, "04:00:00") },
      weg: "/auftraege/V-2026-0151" }),
    f({ schluessel: "uebergabe:1", art: "uebergabe", form: "aufgabe", stufe: "ueberfaellig", gruppe: "technik",
      eskalation: "disposition", kennung: "KR-01", kunde: "Solar Nord GmbH", vorgang: "V-2026-0151",
      belegung_id: "b6", faellig: zeit(-1), daten: { besitz: "frei" }, weg: "/auftraege/V-2026-0151" }),
    f({ schluessel: "uebergabe:2", art: "uebergabe", form: "aufgabe", stufe: "demnaechst", gruppe: "technik",
      eskalation: "disposition", kennung: "MU-01", kunde: "Solar Nord GmbH", vorgang: "V-2026-0151",
      besitzer: { id: "attrappe-fahrer", name: "Fahrer" }, belegung_id: "b19", faellig: zeit(1),
      daten: { besitz: "bediener" }, weg: "/auftraege/V-2026-0151" }),
    /* Dieselben Aufgaben wie im Personal-Band (VERTEILSTAND unten): frei, aus
       der Beispielwoche. Der Weg führt ins Brett, auf den Tag der Aufgabe. */
    ...([["RP-02", "Schulz KG", "V-2026-0139", "uebergabe", 1], ["RL-09", "Schulz KG", "V-2026-0139", "ruecknahme", 3],
      ["MS-11", "Bauer GmbH", "V-2026-0142", "uebergabe", 1], ["RL-11", "Ahlers Bau", "V-2026-0155", "ruecknahme", 3],
    ] as const).map(([k, kunde, vorgang, aufgabe, n]) => f({
      schluessel: `verteilen:${k}:${aufgabe}`, art: "verteilen", form: "aufgabe", stufe: n <= 1 ? "heute" : "demnaechst",
      gruppe: "disposition", kennung: k, kunde, vorgang, belegung_id: k, faellig: zeit(n),
      daten: { aufgabe, tag: werktagAb(tag(0), n) }, weg: `/einteilen?tag=${werktagAb(tag(0), n)}`,
    })),
    f({ schluessel: "freigabe:1", art: "freigabe", form: "entscheidung", stufe: "heute", gruppe: "disposition",
      kennung: "V-2026-0014", kunde: "Beispiel GmbH", vorgang: "V-2026-0014", seit: tag(-1),
      weg: "/auftraege/V-2026-0014" }),
    f({ schluessel: "unterschrift_fehlt:1", art: "unterschrift_fehlt", form: "entscheidung", stufe: "heute",
      gruppe: "disposition", kennung: "AB-22", kunde: "Beispiel GmbH", vorgang: "V-2026-0014",
      daten: { grund: "Kunde nicht vor Ort" }, weg: "/auftraege/V-2026-0014" }),
  ],
};

/* ══════════════ Verteilen im Brett (25.09.2026, 53-verteilen.md) ══════════════

   **Die Aufgaben gehören zu den Balken der Beispielwoche** (`attrappe/daten.ts`,
   `BELEGUNGEN`). Seit im Planungsbrett verteilt wird, stehen Aufgaben und
   Balken nebeneinander — eine Rücknahme von RL-07 an einem Tag, an dem RL-07
   gar nicht zurückkommt, wäre im Prototyp sofort als erfunden zu sehen.
   Deshalb entsteht jede Zeile aus einer festen Maschinenbelegung, und zwar
   in denselben Spalten: Arbeitstage ab dem Anfang des Fensters, wie
   `daten/routen.ts` die Balken legt.

   - Was ab Spalte 0 läuft (`zustand: "laeuft"`), ist draußen: nur noch
     Zurückholen am letzten Tag.
   - Kran (KR-01) und Trecker (TR-03) haben einen Menschen an der Position —
     Lenger und Kruse, ihre Aufgaben sind gebunden.
   - Der Rest ist teils verteilt, teils frei; eine Rücknahme liegt bei
     S. Ott, der die ganze Woche Urlaub hat — damit das Brett zeigt, wovor
     es warnt. Die Radlader verlangen den Führerschein CE (Tieflader).

   **Der Stand lebt**: `ZUTEILEN` merkt sich jede Zuteilung je Aufgabe,
   damit ein Zuteilen im Prototyp sichtbar etwas tut. Neu geladen ist er
   wieder wie hier. */

type Wer = { id: string; name: string } | null;

/** Die Menschen der Beispielwoche — ihre Kennung im Brett ist ihr Name. */
const P = {
  lenger: { id: "M. Lenger", name: "M. Lenger" },
  brand: { id: "T. Brand", name: "T. Brand" },
  kruse: { id: "J. Kruse", name: "J. Kruse" },
  ott: { id: "S. Ott", name: "S. Ott" },
  wilts: { id: "H. Wilts", name: "H. Wilts" },
};

/** Was an den Beispielbalken hängt — Ort, Übergabe, wer schon etwas hat. */
const ZU: Record<string, {
  typ: string; ort: string; ortschaft: string; entfernung: number; verlangt?: string[];
  bediener?: Wer[]; vorbereitung?: Wer; vorbereitet?: boolean; uebergabe?: Wer; ruecknahme?: Wer;
  abholen?: boolean;
}> = {
  b1: { typ: "Radlader 5 t", ort: "Baustelle Nordring", ortschaft: "Cloppenburg", entfernung: 8, verlangt: ["CE"], ruecknahme: P.ott },
  b3: { typ: "Radlader 5 t", ort: "Gewerbegebiet Süd", ortschaft: "Emstek", entfernung: 14, verlangt: ["CE"],
    vorbereitung: P.wilts, uebergabe: P.brand },
  b5: { typ: "Radlader 5 t", ort: "Neubau Kita", ortschaft: "Vechta", entfernung: 21, verlangt: ["CE"],
    vorbereitung: P.brand, uebergabe: P.brand },
  b6: { typ: "Autokran 30 t", ort: "Solarpark Nord, Feld B", ortschaft: "Garrel", entfernung: 17, verlangt: ["KRAN"],
    bediener: [P.lenger, P.wilts] },
  b11: { typ: "Rüttelplatte 500", ort: "Neubau Kita", ortschaft: "Vechta", entfernung: 21 },
  b12: { typ: "Radlader 5 t", ort: "Gewerbegebiet Süd", ortschaft: "Emstek", entfernung: 14, verlangt: ["CE"] },
  b15: { typ: "Minibagger 1,8 t", ort: "Baustelle Nordring", ortschaft: "Cloppenburg", entfernung: 8,
    vorbereitung: P.wilts, vorbereitet: true },
  b17: { typ: "Rüttelplatte 500", ort: "Baustelle Nordring", ortschaft: "Cloppenburg", entfernung: 8 },
  b18: { typ: "Trecker 120 PS", ort: "Hof Hempen", ortschaft: "Garrel", entfernung: 17, verlangt: ["CE"],
    bediener: [P.kruse] },
};

const leer = { wer: null as Wer, zugeteilt_von: null as string | null };
const zuteilung = (wer: Wer | undefined) => ({ wer: wer ?? null, zugeteilt_von: wer ? "Lisa Meyer" : null });

/** Ein Zeitpunkt am Hof als ISO — `07:00` in Europa/Berlin, Sommerzeit reicht für den Prototyp. */
const amHof = (tagText: string, stunde: number) =>
  new Date(`${tagText}T${String(stunde).padStart(2, "0")}:00:00+02:00`).toISOString();

/** Was im Prototyp zugeteilt wurde, je `belegung:art`. */
const zugeteilt = new Map<string, Wer>();

function zeilenAb(von: string): Verteilzeile[] {
  const heute = new Date().toISOString().slice(0, 10);
  const zeilen: Verteilzeile[] = [];
  for (const b of A.BELEGUNGEN) {
    const zu = ZU[b.id];
    if (!zu || !b.fest || b.geschaeft === "sperre") continue;
    const tagVon = werktagAb(von, b.von);
    const tagBis = werktagAb(von, b.bis);
    const draussen = b.zustand === "laeuft" && b.von === 0;
    const bediener = (zu.bediener ?? []).filter((x): x is { id: string; name: string } => !!x);
    const z: Verteilzeile = {
      belegung_id: b.id, kennung: b.res, typ: zu.typ, vorgang: b.vorgang ?? null, kunde: b.kunde,
      beginn: amHof(tagVon, 6 + (b.vonStd ?? 1)), ende: amHof(tagBis, 6 + Math.min((b.bisStd ?? 10) + 1, 11)),
      tag_beginn: tagVon, tag_ende: tagBis, tag_vorbereitung: arbeitstagVorher(tagVon),
      uebergabeart: zu.abholen ? "abholen" : "liefern",
      ort: zu.ort, ortschaft: zu.ortschaft, entfernung: zu.entfernung, verlangt: zu.verlangt ?? [], bediener,
      ausgegeben: draussen || tagVon < heute, zurueck: tagBis < heute,
      vorbereitung: { erledigt: draussen || !!zu.vorbereitet || tagVon < heute, ...zuteilung(zu.vorbereitung) },
      uebergabe: draussen ? { ...leer } : zuteilung(zu.uebergabe),
      ruecknahme: zuteilung(zu.ruecknahme),
    };
    for (const art of ["vorbereitung", "uebergabe", "ruecknahme"] as const) {
      const k = `${b.id}:${art}`;
      if (!zugeteilt.has(k)) continue;
      const neu = zuteilung(zugeteilt.get(k));
      if (art === "vorbereitung") z.vorbereitung = { ...z.vorbereitung, ...neu };
      else z[art] = neu;
    }
    zeilen.push(z);
  }
  return zeilen;
}

const VERTEILLEUTE: Verteilstand["leute"] = [
  { ...P.brand, kuerzel: "TB", rollen: ["technik"], stamm: true, qualifikationen: [{ code: "CE", gueltig_bis: null }] },
  { ...P.kruse, kuerzel: "JK", rollen: ["technik"], stamm: true,
    qualifikationen: [{ code: "CE", gueltig_bis: null }, { code: "KRAN", gueltig_bis: tag(300) }] },
  { ...P.lenger, kuerzel: "ML", rollen: ["technik"], stamm: true, qualifikationen: [{ code: "KRAN", gueltig_bis: tag(360) }] },
  { ...P.wilts, kuerzel: "HW", rollen: ["technik"], stamm: true, qualifikationen: [{ code: "PSAgA", gueltig_bis: tag(120) }] },
  { ...P.ott, kuerzel: "SO", rollen: ["disposition"], stamm: false, qualifikationen: [{ code: "CE", gueltig_bis: tag(35) }] },
];

/** Das Band der Menschen aus den Personenbalken der Beispielwoche. */
function bandAb(von: string): Verteilstand["band"] {
  return A.BELEGUNGEN.filter((b) => A.RESSOURCEN.some((r) => r.id === b.res && r.gruppe === "personal"))
    .map((b) => ({
      person_id: b.res,
      art: b.geschaeft === "sperre" ? (b.grundArt ?? "sperre") : "fest",
      von: amHof(werktagAb(von, b.von), b.geschaeft === "sperre" ? 0 : 6 + (b.vonStd ?? 1)),
      bis: amHof(werktagAb(von, b.bis), b.geschaeft === "sperre" ? 23 : 6 + Math.min((b.bisStd ?? 10) + 1, 11)),
      kennung: b.mit?.find((m) => A.RESSOURCEN.some((r) => r.id === m && r.gruppe === "maschine")) ?? null,
      vorgang: b.vorgang ?? null,
    }));
}

export const VERTEILSTAND = (von: string, bis: string): Verteilstand => ({
  von, bis,
  zeilen: zeilenAb(von),
  leute: VERTEILLEUTE,
  band: bandAb(von),
  qualifikationen: [
    { code: "CE", label: "Führerschein CE" }, { code: "KRAN", label: "Kranführerschein" },
    { code: "PSAgA", label: "PSA gegen Absturz" },
  ],
});

/** Zuteilen im Prototyp — merkt sich die Zuteilung, damit das Brett sie zeigt. */
export function ZUTEILEN(liste: Zuteilung[]): number {
  for (const z of liste) {
    const person = VERTEILLEUTE.find((p) => p.id === z.wer);
    zugeteilt.set(`${z.belegung_id}:${z.art}`, person ? { id: person.id, name: person.name } : null);
  }
  return liste.length;
}
